/**
 * The permissions Cardinal issues for this application.
 *
 * **The numbers are not a hierarchy.** They come from the previous structure, which had modules, sub
 * modules and components; the middle level is gone and its numbers were reused, so a value says
 * nothing about which module it belongs to. `PORTAL_CAN_USE_LICENSES` is 2101 and belongs to
 * **Licenses**, whose module permission is 2500, while 2000 is Permissions.
 * **Read the name, never the range.**
 */
export enum Permission {
  /**
   * **Neither of the two COMMON entries steers anything here.** They govern behaviour in
   * `Eshava.Services.Accounts` and have no counterpart in this frontend. They are listed because
   * Cardinal issues them and a reader will meet them in a permission string -- not because something
   * should start depending on them. Sentinel had a screen gate inferred from a COMMON name it did
   * not have, which locked out correctly configured accounts; that is the mistake this note exists
   * to prevent.
   */
  COMMON_IGNORE_SUBSCRIPTION_ASSIGNMENT = 100,
  COMMON_CAN_USE_OWN_COMPANY_ACCOUNT = 101,
  PORTAL_CAN_USE_MODULE_SETTINGS = 500,
  PORTAL_CAN_USE_MODULE_APPLICATIONS = 1000,
  PORTAL_CAN_USE_APPLICATIONS = 1101,
  PORTAL_CAN_WRITE_APPLICATIONS = 1102,
  PORTAL_CAN_USE_APPLICATION_TYPES = 1103,
  PORTAL_CAN_WRITE_APPLICATION_TYPES = 1104,
  PORTAL_CAN_USE_TENANT_SUBSCRIPTIONS = 1105,
  PORTAL_CAN_WRITE_TENANT_SUBSCRIPTIONS = 1106,
  PORTAL_CAN_USE_MACHINE_TOKENS = 1107,
  PORTAL_CAN_WRITE_MACHINE_TOKENS = 1108,
  PORTAL_CAN_USE_SERVICE_HOOKS = 1109,
  PORTAL_CAN_WRITE_SERVICE_HOOKS = 1110,
  PORTAL_CAN_USE_MODULE_APPLICATION_ACCOUNTS = 1200,
  PORTAL_CAN_USE_APPLICATION_ACCOUNTS = 1201,
  PORTAL_CAN_CREATE_APPLICATIONS_ACCOUNTS = 1202,
  PORTAL_CAN_DELETE_APPLICATIONS_ACCOUNTS = 1203,
  PORTAL_CAN_USE_APPLICATION_ACCOUNT_DETAILS = 1205,
  PORTAL_CAN_USE_APPLICATION_ACCOUNT_ROLES = 1206,
  PORTAL_CAN_WRITE_APPLICATION_ACCOUNT_ROLES = 1207,
  PORTAL_CAN_USE_USER_APPLICATION_ACCOUNTS = 1208,
  PORTAL_CAN_WRITE_USER_APPLICATION_ACCOUNTS = 1209,
  PORTAL_CAN_EDIT_APPLICATIONS_ACCOUNTS = 1210,
  PORTAL_CAN_USE_APPLICATION_ACCOUNT_API_TOKENS = 1211,
  PORTAL_CAN_WRITE_APPLICATION_ACCOUNT_API_TOKENS = 1212,
  PORTAL_CAN_USE_USER_APPLICATION_ACCOUNTS_OVERVIEW = 1301,
  PORTAL_CAN_USE_SYSTEM_USER = 1302,
  PORTAL_CAN_WRITE_SYSTEM_USER = 1303,
  PORTAL_CAN_USE_MODULE_PERMISSIONS = 2000,
  PORTAL_CAN_USE_LICENSES = 2101,
  PORTAL_CAN_WRITE_LICENSES = 2102,
  PORTAL_CAN_USE_LICENSE_TYPES = 2103,
  PORTAL_CAN_WRITE_LICENSE_TYPES = 2104,
  PORTAL_CAN_USE_PERMISSION_STRUCTURE = 2201,
  PORTAL_CAN_WRITE_PERMISSION_STRUCTURE = 2202,
  PORTAL_CAN_USE_PERMISSIONS = 2207,
  PORTAL_CAN_WRITE_PERMISSIONS = 2208,
  PORTAL_CAN_USE_ROLE_TEMPLATES = 2301,
  PORTAL_CAN_WRITE_ROLE_TEMPLATES = 2302,
  PORTAL_CAN_USE_MODULE_LICENSES = 2500
}

/**
 * Unpacks the permission string carried in the user's app metadata.
 *
 * **One character per permission, and the character code is the number.** That is the whole format --
 * `PermissionExtensions` on the server side writes it with `(char)permission` and reads it back with
 * `(int)character`. Nothing is delimited, so nothing has to be parsed.
 *
 * `codePointAt` rather than `charCodeAt`, and the spread rather than `split('')`: both matter only if
 * a value ever landed above 0xFFFF, which none of these do -- but the pair that is correct for every
 * value costs nothing over the pair that is correct for these.
 *
 * An empty or missing string is no permissions, which is not the same as an error: a user who has
 * been given none is a user who may do nothing here.
 */
export function permissionsOf(packed: string | null | undefined): ReadonlySet<number> {
  if (packed === null || packed === undefined || packed === '') {
    return new Set();
  }

  return new Set([...packed].map(character => character.codePointAt(0) ?? 0));
}